/**
 * Single source of truth for market-aware emergency contacts.
 * Pulled from docs/localization/MARKET_REFERENCE.md.
 *
 * Used by:
 * - Chapter 6 Slide 4 (Call your bank)
 * - Chapter 6 Slide 7 (Report it)
 * - Chapter 6 Slide 10 (Finale)
 * - Emergency Toolkit page (planned)
 * - Home page Emergency Card (planned)
 *
 * When MARKET_REFERENCE changes, update here and grep for any inline duplicates.
 */

export type Market = 'US' | 'GB' | 'CA' | 'AU'
export const DEFAULT_MARKET: Market = 'US'

export type ReportingChannel = {
  /** Display name of the org */
  org: string
  /** Phone number, URL, or both */
  detail: string
  /** When to use this channel (one short sentence) */
  whenToUse: string
  /** Optional URL for clickable links */
  url?: string
  /** Optional phone number for tel: links */
  phone?: string
}

export type FraudHotline = {
  number: string
  note: string
  /** True only when there's a single market-wide hotline (e.g. UK 159). Otherwise null. */
  isUniversal: boolean
}

export type EmergencyNumber = {
  label: string
  value: string
  note: string
  /** Optional href for tel: or https: */
  href?: string
}

export type SupportService = {
  org: string
  /** Display string, typically the phone number formatted for the market */
  detail: string
  /** One short sentence on when this helps */
  whenToUse: string
  url?: string
  phone?: string
}

export type CrisisLine = {
  org: string
  detail: string
  phone?: string
}

/**
 * Reporting channels per market, in order of relevance for an everyday consumer scam.
 * Primary channel first.
 */
export const REPORTING_CHANNELS: Record<Market, ReportingChannel[]> = {
  US: [
    {
      org: 'FTC',
      detail: 'reportfraud.ftc.gov',
      whenToUse: 'Start here for any scam, whether or not you lost money.',
      url: 'https://reportfraud.ftc.gov',
    },
    {
      org: 'FBI IC3',
      detail: 'ic3.gov',
      whenToUse: 'Add this if money moved online or the loss was significant.',
      url: 'https://ic3.gov',
    },
    {
      org: 'AARP Fraud Watch Helpline',
      detail: '877-908-3360',
      whenToUse: 'Free human support, especially helpful for older adults.',
      phone: '+18779083360',
    },
  ],
  GB: [
    {
      org: 'Action Fraud',
      detail: 'actionfraud.police.uk • 0300 123 2040',
      whenToUse: 'The national reporting centre for England, Wales, and Northern Ireland.',
      url: 'https://www.actionfraud.police.uk',
      phone: '+443001232040',
    },
    {
      org: 'Police Scotland',
      detail: '101',
      whenToUse: 'In Scotland, Action Fraud doesn’t apply. Call 101 instead.',
      phone: '101',
    },
    {
      org: 'Citizens Advice',
      detail: '0808 223 1133',
      whenToUse: 'Free advice on consumer scams and what to do next.',
      phone: '+448082231133',
    },
  ],
  CA: [
    {
      org: 'Canadian Anti-Fraud Centre',
      detail: 'antifraudcentre-centreantifraude.ca • 1-888-495-8501',
      whenToUse: 'The national reporting centre for fraud and scams.',
      url: 'https://antifraudcentre-centreantifraude.ca',
      phone: '+18884958501',
    },
    {
      org: 'Local police (non-emergency)',
      detail: 'your local non-emergency line',
      whenToUse: 'If you’ve lost money or need an in-person report.',
    },
  ],
  AU: [
    {
      org: 'Scamwatch',
      detail: 'scamwatch.gov.au',
      whenToUse: 'Run by the ACCC. Start here for any scam.',
      url: 'https://www.scamwatch.gov.au',
    },
    {
      org: 'ReportCyber',
      detail: 'cyber.gov.au/report',
      whenToUse: 'Use this if there’s a cybercrime element (hacked accounts, online fraud).',
      url: 'https://www.cyber.gov.au/report',
    },
    {
      org: 'IDCARE',
      detail: '1800 595 160',
      whenToUse: 'Free identity and cyber support if your details have been stolen.',
      phone: '+611800595160',
    },
  ],
}

/**
 * Universal bank fraud hotlines. Only some markets have one.
 */
export const FRAUD_HOTLINES: Record<Market, FraudHotline | null> = {
  GB: {
    number: '159',
    note: 'Free. Connects you straight to your bank’s fraud team. Most major UK banks participate.',
    isUniversal: true,
  },
  US: null,
  CA: null,
  AU: null,
}

/**
 * Three-line "save these numbers" card content per market.
 * Used by Slide 10 Finale and Home page Emergency Card.
 */
export const EMERGENCY_NUMBERS: Record<Market, EmergencyNumber[]> = {
  GB: [
    {
      label: 'Bank fraud hotline',
      value: '159',
      note: 'Free. For participating UK banks.',
      href: 'tel:159',
    },
    {
      label: 'Forward scam texts',
      value: '7726',
      note: 'Free. Works for any suspicious text.',
      href: 'sms:7726',
    },
    {
      label: 'Police',
      value: '999 or 101',
      note: '999 if it’s an emergency. 101 otherwise.',
    },
  ],
  US: [
    {
      label: 'Forward scam texts',
      value: '7726',
      note: 'Free. Works for any suspicious text.',
      href: 'sms:7726',
    },
  ],
  CA: [
    {
      label: 'Forward scam texts',
      value: '7726',
      note: 'Free. Works for any suspicious text.',
      href: 'sms:7726',
    },
  ],
  AU: [
    {
      label: 'Police',
      value: '000 or 131 444',
      note: '000 if it’s an emergency. 131 444 otherwise.',
    },
  ],
}

/**
 * Crisis / "talk to someone now" line for each market. Sits alongside
 * report channels in the toolkit, the user shouldn't have to hunt for
 * mental-health support in a small section at the bottom.
 */
export const CRISIS_LINES: Record<Market, CrisisLine> = {
  US: {
    org: '988 Suicide & Crisis Lifeline',
    detail: 'Call or text 988',
    phone: '988',
  },
  GB: {
    org: 'Samaritans',
    detail: '116 123',
    phone: '116123',
  },
  CA: {
    org: '9-8-8 Suicide Crisis Helpline',
    detail: 'Call or text 988',
    phone: '988',
  },
  AU: {
    org: 'Lifeline',
    detail: '13 11 14',
    phone: '131114',
  },
}

/**
 * Free helplines for emotional support, consumer advice, and victim support
 * after a scam. Distinct from REPORTING_CHANNELS (file a report) and
 * CRISIS_LINES (talk to someone now).
 */
export const SUPPORT_SERVICES: Record<Market, SupportService[]> = {
  US: [
    {
      org: 'Eldercare Locator',
      detail: '1-800-677-1116',
      whenToUse: 'Free helpline for older adults and caregivers.',
      phone: '+18006771116',
    },
  ],
  GB: [
    {
      org: 'Victim Support',
      detail: '0808 168 9111',
      whenToUse: 'Free emotional support and practical help after a crime.',
      phone: '+448081689111',
    },
    {
      org: 'Citizens Advice',
      detail: '0808 223 1133',
      whenToUse: 'Consumer rights and scam guidance.',
      phone: '+448082231133',
    },
    {
      org: 'Age UK',
      detail: '0800 678 1602',
      whenToUse: 'Support specifically for older adults.',
      phone: '+448006781602',
    },
  ],
  CA: [
    {
      org: '211 Canada',
      detail: '211',
      whenToUse: 'Connects you to local victim, social, and crisis services.',
      phone: '211',
    },
  ],
  AU: [
    {
      org: 'IDCARE',
      detail: '1800 595 160',
      whenToUse: 'Free identity and cyber support after a scam.',
      phone: '+611800595160',
    },
    {
      org: '1800RESPECT',
      detail: '1800 737 732',
      whenToUse: 'Counselling and support for older adults experiencing harm.',
      phone: '+611800737732',
    },
  ],
}

export function getReportingChannels(market: string | null | undefined): ReportingChannel[] {
  const m = (market ?? DEFAULT_MARKET) as Market
  return REPORTING_CHANNELS[m] ?? REPORTING_CHANNELS[DEFAULT_MARKET]
}

export function getFraudHotline(market: string | null | undefined): FraudHotline | null {
  const m = (market ?? DEFAULT_MARKET) as Market
  return FRAUD_HOTLINES[m] ?? null
}

export function getEmergencyNumbers(market: string | null | undefined): EmergencyNumber[] {
  const m = (market ?? DEFAULT_MARKET) as Market
  return EMERGENCY_NUMBERS[m] ?? EMERGENCY_NUMBERS[DEFAULT_MARKET]
}

export function getSupportServices(market: string | null | undefined): SupportService[] {
  const m = (market ?? DEFAULT_MARKET) as Market
  return SUPPORT_SERVICES[m] ?? SUPPORT_SERVICES[DEFAULT_MARKET]
}

export function getCrisisLine(market: string | null | undefined): CrisisLine {
  const m = (market ?? DEFAULT_MARKET) as Market
  return CRISIS_LINES[m] ?? CRISIS_LINES[DEFAULT_MARKET]
}

export function getMarketLabel(market: string | null | undefined): string {
  const m = (market ?? DEFAULT_MARKET).toUpperCase()
  if (m === 'GB') return 'UK'
  if (m === 'US') return 'US'
  if (m === 'CA') return 'Canada'
  if (m === 'AU') return 'Australia'
  return m
}
